import React from "react";
import { Link } from "react-router-dom";

//BinaryForge Components
import { Field } from "../../";
import { Note, PillItem } from "../../helpers";
import { PendingUpdate } from "../";
import FlowstopItem from "../../alarm/FlowstopItem";

//Helpers
import {
	formatFlowValue,
	getBatteryChargingString,
	getRelayString,
	getSensorResistanceString,
	getSensorString,
	getWifiRssiString,
	getYesNoString,
} from "../../../helpers/Device";
import { getUiDateTime } from "../../../helpers/DateUtils";
import { inputDeviceOptions } from "../../../config/device";
import { nav } from "../../../config/navigation";

const getFiniteNumber = (value) => {
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const formatTemperature = (value) => {
	const parsed = getFiniteNumber(value);
	return parsed === null ? "---" : parsed.toFixed(1);
};

const buildFlowRows = (flowState) => {
	const rows = [];
	if (flowState?.sensor1) {
		rows.push({
			id: "sensor1",
			sensor: "Flow Sensor 1",
			rateLpm: formatFlowValue(flowState.sensor1.rate_lpm),
			volume10m: formatFlowValue(flowState.sensor1.volume_l_10m),
			volume1h: formatFlowValue(flowState.sensor1.volume_l_1h),
		});
	}
	if (flowState?.sensor2) {
		rows.push({
			id: "sensor2",
			sensor: "Flow Sensor 2",
			rateLpm: formatFlowValue(flowState.sensor2.rate_lpm),
			volume10m: formatFlowValue(flowState.sensor2.volume_l_10m),
			volume1h: formatFlowValue(flowState.sensor2.volume_l_1h),
		});
	}
	return rows;
};

const FlowMetricsTable = ({ rows, isStale }) => {
	if (!rows.length) {
		return null;
	}

	return (
		<div style={{ overflowX: "auto" }}>
			<div style={{ minWidth: "480px" }}>
				<div
					className="grid gapCol-medium gapRow-xsmall fontWeight-bold marginBottom-small"
					style={{
						gridTemplateColumns: "minmax(8ch, 1fr) minmax(8ch, 1fr) minmax(10ch, 1fr) minmax(10ch, 1fr)",
					}}
				>
					<span>Flow Sensor</span>
					<span>Rate (L/min)</span>
					<span>10m Volume (L)</span>
					<span>1h Volume (L)</span>
				</div>
				{rows.map((row) => (
					<div
						key={row.id}
						className={`grid gapCol-medium gapRow-xsmall marginBottom-xsmall ${
							isStale ? "liveStateTableRowStale" : ""
						}`}
						style={{
							gridTemplateColumns:
								"minmax(8ch, 1fr) minmax(8ch, 1fr) minmax(10ch, 1fr) minmax(10ch, 1fr)",
						}}
					>
						<span>{row.sensor}</span>
						<span>{row.rateLpm}</span>
						<span>{row.volume10m}</span>
						<span>{row.volume1h}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const DeviceDetailsLiveStateSection = ({
	t,
	viewModel,
	flowstops = [],
	sensors = [],
	showLiveTelemetry = true,
	embedded = false,
}) => {
	const { pendingUpdates, liveState } = viewModel;
	const { deviceInfo, reportedState, modbusState } = liveState;
	const presence = viewModel?.presence;
	//Offline comes from the connection-lost event; presence is still the source of last seen.
	//No warning here - the overview section above already carries one, and repeating it on the
	//same page is noise.
	const isDeviceOffline = Boolean(viewModel?.offlineState?.isOffline);
	const lastSeenValue = presence?.lastSeenAtSeconds ?? deviceInfo?.updatedAt;
	const flowRows = buildFlowRows(reportedState?.flow);
	const hasRelayStatus = reportedState?.relay?.status !== undefined;
	const hasValve1Status = reportedState?.valves?.valve1?.status !== undefined;
	const hasValve2Status = reportedState?.valves?.valve2?.status !== undefined;
	const hasOutputRelay1Status = reportedState?.outputs?.relay1?.status !== undefined;
	const hasOutputRelay2Status = reportedState?.outputs?.relay2?.status !== undefined;
	const hasValvesAndRelays =
		(deviceInfo?.outputDevice?.length > 0 && !reportedState?.valves && hasRelayStatus) ||
		hasValve1Status ||
		hasValve2Status ||
		hasOutputRelay1Status ||
		hasOutputRelay2Status;
	const hasLeakSensorState =
		deviceInfo?.inputDevice?.length > 0 &&
		(reportedState?.sensor1?.status !== undefined || reportedState?.sensor2?.status !== undefined);
	const hasLeakSensorResistance =
		deviceInfo?.inputDevice?.length > 0 &&
		reportedState?.sensor1?.resistance !== undefined &&
		reportedState?.sensor1?.resistance !== null;
	const hasLeakSensorFault = Boolean(reportedState?.sensor1?.fault);
	const hasLeakDistance =
		deviceInfo?.inputDevice?.includes("RANGER") &&
		modbusState?.leakDistance !== undefined &&
		modbusState?.leakDistance !== null;
	const hasLeakSensorSection = hasLeakSensorState || hasLeakSensorResistance || hasLeakSensorFault || hasLeakDistance;
	const hasTemperatureSection =
		reportedState?.temperature?.ambient_c !== undefined || reportedState?.temperature?.internal_c !== undefined;
	const hasAlarmSection =
		Boolean(reportedState?.alarm?.override_active) || reportedState?.alarm?.audible !== undefined;
	const hasWifiRssi = reportedState?.wifi?.rssi !== undefined && reportedState?.wifi?.rssi !== null;
	const hasBatteryCharging = reportedState?.battery?.enabled && reportedState?.battery?.charging !== undefined;
	const hasBatteryPercentage =
		reportedState?.battery?.enabled &&
		reportedState?.battery?.percentage !== undefined &&
		reportedState?.battery?.percentage !== null;
	const hasConnectivitySection = hasWifiRssi || hasBatteryCharging || hasBatteryPercentage;
	const staleMinutes = Math.round((presence?.staleAfterSeconds || 0) / 60);

	//Disabled valves/relays are configuration, not faults - label and mute them
	const getStatefulResponse = (label, disabled) =>
		disabled ? `${label} (${t("device.state.disabled")})` : label;
	const getValveClass = (status, disabled) =>
		disabled ? "fieldBoolean-offline" : `fieldBoolean-${Boolean(status) ? "valveClosed" : "valveOpen"}`;

	return (
		<div className="flexVert gap-medium">
			{pendingUpdates.length > 0 && (
				<div className="card">
					<header>
						<h3>Pending Updates</h3>
					</header>
					<div className="flexVert gap-small">
						{pendingUpdates.map((update) => (
							<PendingUpdate
								key={update.key}
								label={t(update.labelKey)}
								value={update.value}
								createdBy={update.createdBy}
								createdAt={update.createdAt}
							/>
						))}
					</div>
				</div>
			)}

			<div
				className={
					embedded
						? isDeviceOffline
							? "liveStateCard-offline"
							: ""
						: isDeviceOffline
							? "card liveStateCard-offline"
							: "card"
				}
			>
				{!embedded && (
					<header>
						<h3>{t("device.state.header")}</h3>
					</header>
				)}
				{isDeviceOffline && (
					<Note
						wrapperStyle="liveStateOfflineWarning marginBottom-medium"
						icon="pi pi-exclamation-triangle fontColour-warn"
						messageKey={t("device.state.presence.offlineWarning", {
							lastSeen: getUiDateTime(lastSeenValue, "d MMM yyyy, HH:mm:ss"),
							staleMinutes,
							family: presence?.family || "Device",
						})}
					/>
				)}

				<div className="deviceDetailsSubsectionColumns">
					{flowstops.length > 0 && (
						<section className="deviceDetailsSubsection">
							<h4 className="deviceDetailsSubsectionTitle">{t("device.state.flowstops")}</h4>
							<div className="flex" style={{ gap: "4px", flexWrap: "wrap" }}>
								{flowstops.map((flowstop) => (
									<FlowstopItem key={flowstop.id} flowstopData={flowstop} />
								))}
							</div>
						</section>
					)}

					{sensors.length > 0 && (
						<section className="deviceDetailsSubsection">
							<h4 className="deviceDetailsSubsectionTitle">{t("device.state.triggers")}</h4>
							<div className="flex" style={{ gap: "4px", flexWrap: "wrap" }}>
								{sensors.map((sensor) => (
									<Link key={sensor.id} to={`${nav.device.base}/${sensor.id}`}>
										<PillItem
											type="full"
											value={sensor.name}
											icon={inputDeviceOptions.find((o) => o.value === sensor.inputDevice?.[0])?.icon}
											wrapperStyle="blue1"
										/>
									</Link>
								))}
							</div>
						</section>
					)}
					{hasValvesAndRelays && (
								<section className="deviceDetailsSubsection">
									<h4 className="deviceDetailsSubsectionTitle">Valves and Relays</h4>
									<div className="grid columns-2-Auto gapCol-small gapRow-xsmall">
										{deviceInfo?.outputDevice?.length > 0 &&
											!reportedState?.valves &&
											hasRelayStatus && (
												<Field
													label={t("device.state.relay")}
													highlight
													response={getRelayString(reportedState?.relay?.status)}
													responseClass={[
														"fieldBoolean",
														`fieldBoolean-${
															Boolean(reportedState?.relay?.status)
																? "valveClosed"
																: "valveOpen"
														}`,
													]}
												/>
											)}
										{hasValve1Status && (
											<Field
												label="Valve 1"
												highlight
												response={getStatefulResponse(
													getRelayString(reportedState?.valves?.valve1?.status),
													reportedState?.config?.valves?.valve1?.enabled === false,
												)}
												responseClass={[
													"fieldBoolean",
													getValveClass(
														reportedState?.valves?.valve1?.status,
														reportedState?.config?.valves?.valve1?.enabled === false,
													),
												]}
											/>
										)}
										{hasValve2Status && (
											<Field
												label="Valve 2"
												highlight
												response={getStatefulResponse(
													getRelayString(reportedState?.valves?.valve2?.status),
													reportedState?.config?.valves?.valve2?.enabled === false,
												)}
												responseClass={[
													"fieldBoolean",
													getValveClass(
														reportedState?.valves?.valve2?.status,
														reportedState?.config?.valves?.valve2?.enabled === false,
													),
												]}
											/>
										)}
										{hasOutputRelay1Status && (
											<Field
												label="Output Relay 1"
												highlight
												response={getStatefulResponse(
													getRelayString(reportedState?.outputs?.relay1?.status),
													reportedState?.config?.outputs?.relay1?.enabled === false,
												)}
												responseClass={[
													"fieldBoolean",
													reportedState?.config?.outputs?.relay1?.enabled === false
														? "fieldBoolean-offline"
														: getValveClass(
																reportedState?.outputs?.relay1?.status,
																false,
															),
												]}
											/>
										)}
										{hasOutputRelay2Status && (
											<Field
												label="Output Relay 2"
												highlight
												response={getStatefulResponse(
													getRelayString(reportedState?.outputs?.relay2?.status),
													reportedState?.config?.outputs?.relay2?.enabled === false,
												)}
												responseClass={[
													"fieldBoolean",
													reportedState?.config?.outputs?.relay2?.enabled === false
														? "fieldBoolean-offline"
														: getValveClass(
																reportedState?.outputs?.relay2?.status,
																false,
															),
												]}
											/>
										)}
									</div>
								</section>
							)}

							{hasLeakSensorSection && (
								<section className="deviceDetailsSubsection">
									<h4 className="deviceDetailsSubsectionTitle">Leak Sensor</h4>
									<div className="grid columns-2-Auto gapCol-small gapRow-xsmall">
										{hasLeakSensorState && (
											<Field
												label="State"
												highlight
												response={getSensorString(
													reportedState.sensor1?.status | reportedState.sensor2?.status,
												)}
												responseClass={[
													"fieldBoolean",
													`fieldBoolean-${!Boolean(reportedState.sensor1?.status | reportedState.sensor2?.status)}`,
												]}
											/>
										)}
										{hasLeakSensorResistance && (
											<Field
												label="Resistance"
												highlight
												response={getSensorResistanceString(reportedState.sensor1?.resistance)}
											/>
										)}
										{hasLeakSensorFault && (
											<Field label="Fault" highlight response={"Open/Short"} />
										)}
										{hasLeakDistance && (
											<Field
												label="Leak Distance"
												highlight
												response={modbusState?.leakDistance || "---"}
												responseUnit={!isNaN(modbusState?.leakDistance) && "meters"}
											/>
										)}
									</div>
								</section>
							)}

					{showLiveTelemetry && flowRows.length > 0 && (
						<section className="deviceDetailsSubsection deviceDetailsSubsection--full">
							<h4 className="deviceDetailsSubsectionTitle">Flow Sensors</h4>
							<FlowMetricsTable rows={flowRows} isStale={isDeviceOffline} />
						</section>
					)}

					{hasAlarmSection && (
						<section className="deviceDetailsSubsection">
							<h4 className="deviceDetailsSubsectionTitle">Alarms</h4>
							<div className="grid columns-2-Auto gapCol-small gapRow-xsmall">
								<Field
									label="Override Active"
									response={getYesNoString(reportedState?.alarm?.override_active)}
									responseClass={[
										"fieldBoolean",
										`fieldBoolean-${reportedState?.alarm?.override_active ? "attention" : "neutral"}`,
									]}
								/>
								{reportedState?.alarm?.override_active && (
									<Field
										label="Override Remaining"
										highlight
										response={reportedState?.alarm?.override_remaining_s}
										responseUnit="secs"
									/>
								)}
								{reportedState?.alarm?.audible !== undefined && (
									<Field
										label="Audible Alarm"
										highlight
										response={getYesNoString(reportedState?.alarm?.audible)}
										responseClass={[
											"fieldBoolean",
											`fieldBoolean-${reportedState?.alarm?.audible ? "attention" : "neutral"}`,
										]}
									/>
								)}
							</div>
						</section>
					)}

					{showLiveTelemetry && hasTemperatureSection && (
								<section className="deviceDetailsSubsection">
									<h4 className="deviceDetailsSubsectionTitle">Temperature</h4>
									<div className="grid columns-2-Auto gapCol-small gapRow-xsmall">
										{reportedState?.temperature?.ambient_c !== undefined && (
											<Field
												label="Ambient Temp"
												highlight
												response={formatTemperature(reportedState?.temperature?.ambient_c)}
												responseUnit="°C"
											/>
										)}
										{reportedState?.temperature?.internal_c !== undefined && (
											<Field
												label="Internal Temp"
												highlight
												response={formatTemperature(reportedState?.temperature?.internal_c)}
												responseUnit="°C"
											/>
										)}
									</div>
								</section>
							)}

					{showLiveTelemetry && hasConnectivitySection && (
								<section className="deviceDetailsSubsection">
									<h4 className="deviceDetailsSubsectionTitle">Connectivity</h4>
									<div className="grid columns-2-Auto gapCol-small gapRow-xsmall">
										{hasWifiRssi && (
											<Field
												label={t("device.state.wifiRssi")}
												highlight
												response={getWifiRssiString(reportedState?.wifi?.rssi)}
											/>
										)}
										{hasBatteryCharging && (
											<Field
												label={t("device.state.battCharging")}
												highlight
												response={getBatteryChargingString(reportedState?.battery?.charging)}
												responseClass={[
													"fieldBoolean",
													`fieldBoolean-${reportedState?.battery?.charging ? "true" : "neutral"}`,
												]}
											/>
										)}
										{hasBatteryPercentage && (
											<Field
												label={t("device.state.battPercentage")}
												highlight
												response={reportedState?.battery?.percentage}
												responseUnit="%"
											/>
										)}
									</div>
								</section>
							)}
				</div>
			</div>
		</div>
	);
};

export default DeviceDetailsLiveStateSection;
